import { createElement } from 'react';
import { Flow } from '../Layouts/Flow';
import { Heading } from '../Heading';
import { Text } from '../Text';
import { SVG } from '../SVG';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { postfixTestId } from '../../util/testId';
import type { ZeroStateProps } from './types';
import { BusyStateIndicator } from '../BusyStateIndicator';
import { Button } from '../Button';
import cx from 'classnames';
import cn from './ZeroState.css.json';
import './ZeroState.css';

export const ZeroState = function (props: ZeroStateProps) {
  const { text, heading, icon, disabled, tokens: zsTokens, busyStateIndicatorProps } = props;

  const dt = useDesignTokens();
  const tokens = renderInlineCssVars(
    {
      'mg-s': dt.spacing.core.s,
      'font-weight-thick': dt.fontNova.weight.thick,
      'heading-font-size': zsTokens?.heading?.fontSize ?? dt.fontNova.size.h5,
      'heading-fg': disabled
        ? zsTokens?.heading?.color ?? dt.stateStandardNova.disabled.fg
        : zsTokens?.heading?.color ?? dt.c.colorTextDefault,
      'text-font-size': zsTokens?.text?.fontSize ?? dt.fontNova.size.m,
      'text-fg': disabled
        ? zsTokens?.text?.color ?? dt.stateStandardNova.disabled.fg
        : zsTokens?.text?.color ?? dt.c.colorTextDefault,
    },
    'zero-state'
  );

  return (
    <div
      role="group"
      aria-label={props.ariaLabel}
      aria-labelledby={props.ariaLabelledBy}
      data-testid={props.testId}
      className={cx(cn.zeroState, props.className, props.alignTop && cn.alignTop)}
      style={{ ...tokens, ...props.style }}
    >
      {icon ? (
        <SVG
          style={{ order: '1' }}
          icon={icon}
          type="zerostate"
          testId={postfixTestId(props.testId, 'icon')}
          disabled={disabled}
        />
      ) : null}
      {heading ? (
        <Heading
          style={{ order: '3' }}
          wrapText
          level={5}
          className={cn.heading}
          testId={postfixTestId(props.testId, 'heading')}
          disabled={disabled}
        >
          {heading}
        </Heading>
      ) : null}
      {text ? (
        <Text
          style={{ order: '4' }}
          className={cn.text}
          focusable
          ariaLabel={heading ? `${heading}. ${text}` : undefined}
          testId={postfixTestId(props.testId, 'text')}
          disabled={disabled}
        >
          {text}
        </Text>
      ) : null}
      {props.actionButtonOneProps ? (
        <Flow
          style={{ order: '5' }}
          wrap
          spacing="s"
          className={cn.buttonGroup}
          data-testid={postfixTestId(props.testId, 'flow')}
        >
          {props.actionButtonOneProps ? (
            <Button
              {...props.actionButtonOneProps}
              disabled={disabled ?? props.actionButtonOneProps.disabled}
            />
          ) : null}
          {props.actionButtonTwoProps ? (
            <Button
              {...props.actionButtonTwoProps}
              disabled={disabled ?? props.actionButtonTwoProps.disabled}
            />
          ) : null}
          {props.actionButtonThreeProps ? (
            <Button
              {...props.actionButtonThreeProps}
              disabled={disabled ?? props.actionButtonThreeProps.disabled}
            />
          ) : null}
        </Flow>
      ) : null}
      <BusyStateIndicator style={{ order: '2' }} {...busyStateIndicatorProps} />
    </div>
  );
};
